import { SearchRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SearchFilter from "../../../../components/filters/SearchFilter";
import { Toast } from "../../../../components/Toast";
import { getEnvStorageKey } from "../../../../utils/environment";
import IntegrationRowFilter from "../../../posture/steps/components/IntegrationRowFilter";
import { IntegrationsMapping } from "../../integrations/IntegrationsMapping";
import {
  isRawDataField,
  RAW_DATA_DELIMITER,
} from "../entitiesTable/fieldUtils";

const STORAGE_KEY_PREFIX = "entity_visible_columns_";
const EMPTY_FIELDS = [];
const buildStorageKey = (entityType) =>
  getEnvStorageKey(`${STORAGE_KEY_PREFIX}${entityType}`);

export default function ColumnSettingsModal({
  entities,
  open,
  onClose,
  entityType,
  onColumnsChange,
}) {
  const colors = useColors();
  const { getEntityFields } = useAPI();
  const [selectedFields, setSelectedFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState({ source_integration: {} });

  const { data: fieldsResponse, isLoading } = useQuery({
    queryKey: ["entityFields", entityType],
    queryFn: () => getEntityFields(entityType),
    select: (response) => response.data,
    enabled: open && !!entityType,
  });

  const availableFields = useMemo(
    () => getUniqueFields(fieldsResponse ?? EMPTY_FIELDS),
    [fieldsResponse]
  );

  const sourceOptions = getSourceOptions(availableFields);

  const selectedSource = getSelectedSource(sourceFilter);

  const filteredFields = getFilteredFields(
    availableFields,
    searchTerm,
    selectedSource
  );

  useEffect(() => {
    if (!open) return;
    const storageKey = buildStorageKey(entityType);
    const savedFields = localStorage.getItem(storageKey);

    if (savedFields) {
      const parsed = JSON.parse(savedFields);
      setSelectedFields(parsed);
    } else {
      setSelectedFields([]);
    }
  }, [open, entityType]);

  const handleFieldToggle = (fieldName) => {
    const newSelected = selectedFields.includes(fieldName)
      ? selectedFields.filter((f) => f !== fieldName)
      : [...selectedFields, fieldName];

    setSelectedFields(newSelected);
  };

  const handleSave = async () => {
    try {
      const storageKey = buildStorageKey(entityType);
      localStorage.setItem(storageKey, JSON.stringify(selectedFields));
      onColumnsChange(selectedFields.length > 0 ? selectedFields : null);
      setSearchTerm("");
      setSourceFilter({ source_integration: {} });
      onClose();
    } catch (error) {
      Toast.error("Failed to save column settings");
    }
  };

  const saveColumnsMutation = useMutation({
    mutationFn: handleSave,
  });

  const handleCancel = () => {
    setSearchTerm("");
    setSourceFilter({ source_integration: {} });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.modal_bg,
          borderRadius: "10px",
          boxShadow: colors.dropdown_menu_shadow,
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: "20px 24px 12px 24px",
          color: colors.text_primary,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "22px",
            color: colors.text_primary,
          }}
        >
          Configure Table Columns
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            fontSize: "13px",
            color: colors.text_secondary,
          }}
        >
          Select which attributes to display in the table
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: "0 24px 16px 24px" }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress size={40} sx={{ color: colors.primary_main }} />
          </Box>
        ) : availableFields.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: colors.text_secondary, fontSize: "13px" }}
          >
            No custom attributes available for this entity type
          </Typography>
        ) : (
          <Box>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                placeholder="Search attributes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded
                        sx={{
                          color: colors.text_disabled,
                          width: "16px",
                          height: "16px",
                        }}
                      />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: "13px",
                    color: colors.text_primary,
                  },
                }}
                sx={{
                  backgroundColor: colors.common_white,
                  "& .MuiOutlinedInput-root": {
                    height: "36px",
                    "& fieldset": {
                      borderRadius: "8px",
                      borderColor: colors.textfield_border,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.textfield_border_hover,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary_main,
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.text_disabled,
                    opacity: 1,
                  },
                }}
              />
              {sourceOptions.length > 0 && (
                <SearchFilter
                  filterName={"Source"}
                  fieldName={"source_integration"}
                  options={sourceOptions}
                  appliedFilters={sourceFilter}
                  setAppliedFilters={setSourceFilter}
                />
              )}
            </Box>

            <Box
              sx={{
                height: "350px",
                minHeight: "350px",
                overflowY: "auto",
                paddingRight: 1,
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: colors.text_secondary,
                  borderRadius: "2px",
                },
              }}
            >
              <FormGroup>
                {filteredFields.map((field) => {
                  const displayName = isRawDataField(field.field_name)
                    ? field.field_name.split(RAW_DATA_DELIMITER).pop()
                    : field.field_name;

                  return (
                    <FormControlLabel
                      key={
                        field.field_name +
                        field.source_integration +
                        field.entity_type
                      }
                      control={
                        <Checkbox
                          checked={selectedFields.includes(field.field_name)}
                          onChange={() => handleFieldToggle(field.field_name)}
                          sx={{
                            padding: "4px 9px",
                            color: colors.action_active,
                            "&.Mui-checked": {
                              color: colors.primary_main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: colors.text_primary,
                              lineHeight: "18px",
                            }}
                          >
                            {displayName}
                          </Typography>
                          {field.source_integration && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                lineHeight: "14px",
                                color: colors.text_secondary,
                              }}
                            >
                              Source: {field.source_integration}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{
                        marginBottom: "2px",
                        marginLeft: 0,
                      }}
                    />
                  );
                })}
                {filteredFields.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      textAlign: "center",
                      fontSize: "13px",
                      color: colors.text_secondary,
                    }}
                  >
                    No attributes match your search
                  </Typography>
                )}
              </FormGroup>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          onClick={handleCancel}
          sx={{
            color: colors.text_secondary,
            textTransform: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: colors.action_hover,
            },
          }}
        >
          Cancel
        </Button>
        <ContainedButton
          onClick={() => saveColumnsMutation.mutate()}
          color="primary"
          size="medium"
          loading={saveColumnsMutation.isPending}
        >
          Save
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

function getSourceOptions(availableFields) {
  const sources = availableFields
    .map((field) => field.source_integration)
    .filter(Boolean);
  return [...new Set(sources)]
    .filter((source) => IntegrationsMapping[source])
    .sort()
    .map((source) => ({
      name: source,
      displayName: IntegrationsMapping[source].displayName,
      label: <IntegrationRowFilter name={source} />,
    }));
}

function getSelectedSource(sourceFilter) {
  const filters = sourceFilter.source_integration;
  return Object.keys(filters).find((key) => filters[key]) || "";
}

function getFilteredFields(availableFields, searchTerm, selectedSource) {
  let fields = availableFields;

  if (selectedSource) {
    fields = fields.filter(
      (field) => field.source_integration === selectedSource
    );
  }

  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    fields = fields.filter((field) => {
      const displayName = isRawDataField(field.field_name)
        ? field.field_name.split(RAW_DATA_DELIMITER).pop()
        : field.field_name;
      return displayName.toLowerCase().includes(lowerSearch);
    });
  }

  return fields;
}

function getUniqueFields(fields) {
  const seen = new Set();
  return fields.filter((field) => {
    const key = `${normalizeFieldName(field)}|${normalizeSource(field)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeFieldName(field) {
  const name = isRawDataField(field?.field_name)
    ? field.field_name.split(RAW_DATA_DELIMITER).pop()
    : field?.field_name;
  return name?.toString().trim().toLowerCase() ?? "";
}

function normalizeSource(field) {
  return field?.source_integration?.toString().trim().toLowerCase() ?? "";
}

export function getVisibleColumnsForEntity(entityType) {
  const storageKey = buildStorageKey(entityType);
  const savedFields = localStorage.getItem(storageKey);
  return savedFields ? JSON.parse(savedFields) : null;
}
